<template>
  <div class="sales-overview" :class="{'is-compact': compact}">
    <div v-if="loading" class="sales-loading">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mb-0 mt-3 text-muted">در حال بارگذاری آمار فروش...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger mb-0">
      {{ error }}
    </div>

    <template v-else>
      <div class="row">
        <div
          v-for="card in periodCards"
          :key="card.key"
          class="col-xl-3 col-md-6 mb-3"
        >
          <div class="kpi-card" :class="'kpi-' + card.key">
            <div class="kpi-icon">
              <i :class="card.icon"></i>
            </div>
            <div class="kpi-body">
              <span class="kpi-label">{{ card.label }}</span>
              <strong class="kpi-amount">{{ formatMoney(card.amount) }}</strong>
              <span class="kpi-meta">{{ formatCount(card.count) }} سفارش</span>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-8 mb-3">
          <div class="stat-panel h-100">
            <div class="stat-panel-head daily-head">
              <div>
                <h5 class="mb-1">فروش روزانه</h5>
                <p class="text-muted mb-0 small">ماه را انتخاب کنید یا با دکمه‌ها جابه‌جا شوید</p>
              </div>
              <div class="month-nav">
                <button type="button" class="month-nav-btn" title="ماه قبل" @click="shiftMonth(-1)">
                  <i class="bi bi-chevron-right"></i>
                </button>
                <select v-model.number="selectedMonth" class="month-nav-select" @change="loadDaily">
                  <option v-for="month in monthOptions" :key="month.value" :value="month.value">
                    {{ month.name }}
                  </option>
                </select>
                <select v-model.number="selectedYear" class="month-nav-select year-select" @change="loadDaily">
                  <option v-for="year in yearOptions" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
                <button type="button" class="month-nav-btn" title="ماه بعد" @click="shiftMonth(1)">
                  <i class="bi bi-chevron-left"></i>
                </button>
              </div>
            </div>
            <div v-if="dailyLoading" class="empty-chart">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mb-0 mt-3">در حال بارگذاری این ماه...</p>
            </div>
            <div v-else-if="daily.length" class="chart-wrap">
              <apexchart
                :key="'daily-' + selectedYear + '-' + selectedMonth"
                type="line"
                :height="compact ? 260 : 340"
                :options="dailyOptions"
                :series="dailySeries"
              />
            </div>
            <div v-else class="empty-chart">
              <i class="bi bi-graph-up"></i>
              <p class="mb-0">آمار روزانه این ماه در دسترس نیست.</p>
            </div>
          </div>
        </div>

        <div class="col-lg-4 mb-3">
          <div class="stat-panel h-100 best-day-panel">
            <h5 class="mb-3">بهترین روز فروش</h5>
            <template v-if="bestDay.jalali_date">
              <div class="best-day-date">{{ formattedBestDay }}</div>
              <div class="best-day-amount">{{ formatMoney(bestDay.sales_amount) }}</div>
              <div class="best-day-grid">
                <div>
                  <span>سفارش</span>
                  <strong>{{ formatCount(bestDay.orders_count) }}</strong>
                </div>
                <div>
                  <span>تجمعی</span>
                  <strong>{{ formatMoney(bestDay.cumulative_sales_amount) }}</strong>
                </div>
              </div>
            </template>
            <p v-else class="text-muted mb-0">داده‌ای برای بهترین روز وجود ندارد.</p>

            <div class="growth-box mt-4" :class="growthClass">
              <span>رشد نسبت به ماه قبل</span>
              <strong>{{ formattedGrowth }}</strong>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="!compact">
        <div class="col-lg-7 mb-3">
          <div class="stat-panel h-100">
            <div class="stat-panel-head">
              <div>
                <h5 class="mb-1">فروش ماهانه</h5>
                <p class="text-muted mb-0 small">روند مبلغ فروش در ماه‌های سال</p>
              </div>
            </div>
            <div v-if="monthly.length" class="chart-wrap">
              <apexchart
                type="bar"
                height="320"
                :options="monthlyOptions"
                :series="monthlySeries"
              />
            </div>
            <div v-else class="empty-chart">
              <i class="bi bi-bar-chart"></i>
              <p class="mb-0">آمار ماهانه در دسترس نیست.</p>
            </div>
          </div>
        </div>

        <div class="col-lg-5 mb-3">
          <div class="stat-panel h-100">
            <div class="stat-panel-head">
              <div>
                <h5 class="mb-1">مقایسه بازه‌ها</h5>
                <p class="text-muted mb-0 small">مبلغ فروش امروز، این ماه، امسال و کل</p>
              </div>
            </div>
            <div class="chart-wrap">
              <apexchart
                type="bar"
                height="320"
                :options="periodOptions"
                :series="periodSeries"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div
          v-for="item in visibleHighlights"
          :key="item.key"
          class="col-xl-3 col-md-6 mb-3"
        >
          <div class="highlight-card">
            <div class="highlight-icon" :style="{ background: item.bg, color: item.color }">
              <i :class="item.icon"></i>
            </div>
            <div>
              <span class="highlight-label">{{ item.label }}</span>
              <strong class="highlight-value">{{ item.value }}</strong>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {formatNumber} from '@moeinmdn/mixitweb-front-modules/src/plugin/class/Converter'

const JALALI_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
]

function currentJalaliDate() {
  return {
    year: Number(
      new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', {year: 'numeric'}).format(new Date())
    ),
    month: parseInt(new Date().toLocaleDateString('fa-IR-u-nu-latn', {month: '2-digit'}), 10),
  }
}

const chartFont = 'Peyda, Tahoma, sans-serif'
const primary = '#eb3d60'
const indigo = '#6366f1'
const teal = '#14b8a6'

export default {
  name: 'SalesOverview',
  components: {apexchart: VueApexCharts},
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const current = currentJalaliDate()
    return {
      loading: true,
      dailyLoading: false,
      error: null,
      selectedYear: current.year,
      selectedMonth: current.month,
      sales: {
        total: {orders_count: 0, sales_amount: 0},
        today: {orders_count: 0, sales_amount: 0},
        this_month: {orders_count: 0, sales_amount: 0},
        this_year: {orders_count: 0, sales_amount: 0},
        highlights: {
          average_order_amount: 0,
          items_sold: 0,
          paying_customers_count: 0,
          repeat_customers_count: 0,
          discount_amount: 0,
          biggest_order_amount: 0,
          growth_vs_last_month_percent: 0,
          best_day: {},
        },
        monthly: [],
        daily: [],
      },
    }
  },
  computed: {
    highlights() {
      return this.sales.highlights || {}
    },
    bestDay() {
      return this.highlights.best_day || {}
    },
    monthly() {
      return this.sales.monthly || []
    },
    daily() {
      return this.sales.daily || []
    },
    monthOptions() {
      return JALALI_MONTHS.map((name, index) => ({value: index + 1, name}))
    },
    yearOptions() {
      const currentYear = currentJalaliDate().year
      const list = []
      for (let i = -6; i <= 1; i++) {
        list.push(currentYear + i)
      }
      return list
    },
    periodCards() {
      return [
        {
          key: 'total',
          label: 'کل فروش',
          icon: 'bi bi-wallet2',
          amount: this.sales.total.sales_amount,
          count: this.sales.total.orders_count,
        },
        {
          key: 'today',
          label: 'امروز',
          icon: 'bi bi-calendar-day',
          amount: this.sales.today.sales_amount,
          count: this.sales.today.orders_count,
        },
        {
          key: 'month',
          label: 'این ماه',
          icon: 'bi bi-calendar3',
          amount: this.sales.this_month.sales_amount,
          count: this.sales.this_month.orders_count,
        },
        {
          key: 'year',
          label: 'امسال',
          icon: 'bi bi-calendar-check',
          amount: this.sales.this_year.sales_amount,
          count: this.sales.this_year.orders_count,
        },
      ]
    },
    highlightItems() {
      return [
        {
          key: 'avg',
          label: 'میانگین مبلغ سفارش',
          value: this.formatMoney(this.highlights.average_order_amount),
          icon: 'bi bi-receipt',
          color: primary,
          bg: 'rgba(235, 61, 96, 0.12)',
        },
        {
          key: 'items',
          label: 'اقلام فروخته‌شده',
          value: this.formatCount(this.highlights.items_sold),
          icon: 'bi bi-box-seam',
          color: indigo,
          bg: 'rgba(99, 102, 241, 0.12)',
        },
        {
          key: 'paying',
          label: 'مشتریان پرداخت‌کننده',
          value: this.formatCount(this.highlights.paying_customers_count),
          icon: 'bi bi-people',
          color: '#0ea5e9',
          bg: 'rgba(14, 165, 233, 0.12)',
        },
        {
          key: 'repeat',
          label: 'مشتریان تکراری',
          value: this.formatCount(this.highlights.repeat_customers_count),
          icon: 'bi bi-arrow-repeat',
          color: teal,
          bg: 'rgba(20, 184, 166, 0.12)',
        },
        {
          key: 'discount',
          label: 'مجموع تخفیف',
          value: this.formatMoney(this.highlights.discount_amount),
          icon: 'bi bi-tag',
          color: '#f59e0b',
          bg: 'rgba(245, 158, 11, 0.14)',
        },
        {
          key: 'biggest',
          label: 'بزرگ‌ترین سفارش',
          value: this.formatMoney(this.highlights.biggest_order_amount),
          icon: 'bi bi-trophy',
          color: '#8b5cf6',
          bg: 'rgba(139, 92, 246, 0.12)',
        },
        {
          key: 'orders',
          label: 'کل سفارش‌ها',
          value: this.formatCount(this.sales.total.orders_count),
          icon: 'bi bi-bag-check',
          color: '#ef4444',
          bg: 'rgba(239, 68, 68, 0.12)',
        },
        {
          key: 'growth',
          label: 'رشد ماهانه',
          value: this.formattedGrowth,
          icon: this.growthValue >= 0 ? 'bi bi-graph-up-arrow' : 'bi bi-graph-down-arrow',
          color: this.growthValue >= 0 ? '#16a34a' : primary,
          bg: this.growthValue >= 0 ? 'rgba(22, 163, 74, 0.12)' : 'rgba(235, 61, 96, 0.12)',
        },
      ]
    },
    visibleHighlights() {
      if (!this.compact) {
        return this.highlightItems
      }
      const keys = ['avg', 'paying', 'biggest', 'growth']
      return this.highlightItems.filter((item) => keys.indexOf(item.key) !== -1)
    },
    growthValue() {
      const value = Number(this.highlights.growth_vs_last_month_percent)
      return isNaN(value) ? 0 : value
    },
    formattedGrowth() {
      const sign = this.growthValue > 0 ? '+' : ''
      return sign + this.growthValue + '٪'
    },
    growthClass() {
      return this.growthValue >= 0 ? 'growth-up' : 'growth-down'
    },
    formattedBestDay() {
      if (!this.bestDay.jalali_date) {
        return ''
      }
      const monthName = JALALI_MONTHS[(this.bestDay.month || 1) - 1] || ''
      return (this.bestDay.day || '') + ' ' + monthName + ' ' + this.formatNumberSafe(this.bestDay.year)
    },
    dailyLabels() {
      return this.daily.map((item) => String(item.day))
    },
    dailySeries() {
      return [
        {
          name: 'مبلغ فروش',
          type: 'area',
          data: this.daily.map((item) => Number(item.sales_amount) || 0),
        },
        {
          name: 'تعداد سفارش',
          type: 'line',
          data: this.daily.map((item) => Number(item.orders_count) || 0),
        },
      ]
    },
    dailyOptions() {
      const self = this
      return {
        chart: {
          fontFamily: chartFont,
          toolbar: {show: false},
          zoom: {enabled: false},
          stacked: false,
        },
        colors: [primary, indigo],
        dataLabels: {enabled: false},
        stroke: {curve: 'smooth', width: [3, 3]},
        fill: {
          type: ['gradient', 'solid'],
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          categories: this.dailyLabels,
          labels: {style: {fontFamily: chartFont}},
          title: {text: 'روز ماه', style: {fontFamily: chartFont}},
        },
        yaxis: [
          {
            labels: {
              formatter(value) {
                return self.formatNumberSafe(value)
              },
            },
            title: {text: 'مبلغ', style: {fontFamily: chartFont}},
          },
          {
            opposite: true,
            labels: {
              formatter(value) {
                return Math.round(value)
              },
            },
            title: {text: 'سفارش', style: {fontFamily: chartFont}},
          },
        ],
        tooltip: {
          shared: true,
          y: {
            formatter(value, opts) {
              if (opts && opts.seriesIndex === 1) {
                return self.formatCount(value)
              }
              return self.formatMoney(value)
            },
          },
        },
        legend: {fontFamily: chartFont, position: 'top'},
        grid: {borderColor: '#f0f0f0', strokeDashArray: 4},
      }
    },
    monthlySeries() {
      return [
        {
          name: 'مبلغ فروش',
          data: this.monthly.map((item) => Number(item.sales_amount) || 0),
        },
      ]
    },
    monthlyOptions() {
      const self = this
      return {
        chart: {
          fontFamily: chartFont,
          toolbar: {show: false},
        },
        colors: [primary],
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '42%',
            distributed: false,
          },
        },
        dataLabels: {enabled: false},
        xaxis: {
          categories: this.monthly.map((item) => {
            const month = JALALI_MONTHS[(item.month || 1) - 1] || ''
            return month + ' ' + (item.year || '')
          }),
          labels: {style: {fontFamily: chartFont}},
        },
        yaxis: {
          labels: {
            formatter(value) {
              return self.formatNumberSafe(value)
            },
          },
        },
        tooltip: {
          y: {
            formatter(value) {
              return self.formatMoney(value)
            },
          },
        },
        grid: {borderColor: '#f0f0f0', strokeDashArray: 4},
      }
    },
    periodSeries() {
      return [
        {
          name: 'مبلغ فروش',
          data: [
            Number(this.sales.today.sales_amount) || 0,
            Number(this.sales.this_month.sales_amount) || 0,
            Number(this.sales.this_year.sales_amount) || 0,
            Number(this.sales.total.sales_amount) || 0,
          ],
        },
      ]
    },
    periodOptions() {
      const self = this
      return {
        chart: {
          fontFamily: chartFont,
          toolbar: {show: false},
        },
        colors: [teal, indigo, primary, '#1e1e1e'],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 8,
            barHeight: '52%',
            distributed: true,
          },
        },
        dataLabels: {enabled: false},
        xaxis: {
          categories: ['امروز', 'این ماه', 'امسال', 'کل'],
          labels: {
            formatter(value) {
              return self.formatNumberSafe(value)
            },
          },
        },
        yaxis: {
          labels: {style: {fontFamily: chartFont}},
        },
        legend: {show: false},
        tooltip: {
          y: {
            formatter(value) {
              return self.formatMoney(value)
            },
          },
        },
        grid: {borderColor: '#f0f0f0', strokeDashArray: 4},
      }
    },
  },
  methods: {
    formatNumber,
    formatNumberSafe(value) {
      return formatNumber(value || 0)
    },
    formatCount(value) {
      return formatNumber(value || 0)
    },
    formatMoney(value) {
      return formatNumber(value || 0) + ' تومان'
    },
    salesQuery() {
      return {
        year: this.selectedYear,
        month: this.selectedMonth,
      }
    },
    shiftMonth(step) {
      let month = Number(this.selectedMonth) + step
      let year = Number(this.selectedYear)
      if (month < 1) {
        month = 12
        year -= 1
      } else if (month > 12) {
        month = 1
        year += 1
      }
      this.selectedMonth = month
      this.selectedYear = year
      this.loadDaily()
    },
    async loadDaily() {
      this.dailyLoading = true
      try {
        const res = await this.$store.dispatch('Statistic/sales', this.salesQuery())
        const data = res.data.data || {}
        this.$set(this.sales, 'daily', data.daily || [])
      } catch (e) {
        this.$set(this.sales, 'daily', [])
      } finally {
        this.dailyLoading = false
      }
    },
    async loadSales() {
      this.loading = true
      this.error = null
      try {
        const res = await this.$store.dispatch('Statistic/sales', this.salesQuery())
        this.sales = Object.assign({}, this.sales, res.data.data || {})
        const firstDay = (this.sales.daily && this.sales.daily[0]) || null
        if (firstDay && firstDay.year && firstDay.month) {
          this.selectedYear = Number(firstDay.year)
          this.selectedMonth = Number(firstDay.month)
        }
      } catch (e) {
        this.error = 'بارگذاری آمار فروش با خطا مواجه شد.'
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.loadSales()
  },
}
</script>

<style scoped>
.sales-overview {
  margin-bottom: 1.5rem;
}

.sales-loading {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 18px;
  min-height: 112px;
  color: #fff;
  box-shadow: 0 12px 28px rgba(30, 30, 30, 0.12);
}

.kpi-total {
  background: linear-gradient(135deg, #eb3d60 0%, #f4657e 100%);
}

.kpi-today {
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%);
}

.kpi-month {
  background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%);
}

.kpi-year {
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.kpi-body {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  opacity: 0.92;
  font-size: 13px;
}

.kpi-amount {
  font-size: 18px;
  line-height: 1.6;
}

.kpi-meta {
  font-size: 12px;
  opacity: 0.88;
}

.stat-panel {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(30, 30, 30, 0.06);
}

.stat-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.stat-panel h5 {
  font-weight: 800;
  color: #1e1e1e;
}

.chip {
  background: rgba(235, 61, 96, 0.1);
  color: #eb3d60;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  white-space: nowrap;
}

.daily-head {
  flex-wrap: wrap;
}

.month-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  padding: 4px;
}

.month-nav-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #fff;
  color: #eb3d60;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(30, 30, 30, 0.06);
}

.month-nav-btn:hover {
  background: #eb3d60;
  color: #fff;
}

.month-nav-select {
  border: none;
  background: transparent;
  height: 36px;
  padding: 0 8px;
  color: #1e1e1e;
  font-weight: 700;
  max-width: 110px;
}

.month-nav-select.year-select {
  max-width: 82px;
}

.month-nav-select:focus {
  outline: none;
}

.chart-wrap {
  direction: ltr;
}

.empty-chart {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8a8a8a;
  gap: 10px;
}

.is-compact .empty-chart {
  min-height: 200px;
}

.empty-chart i {
  font-size: 36px;
  color: #eb3d60;
}

.best-day-date {
  font-size: 15px;
  color: #6b7280;
}

.best-day-amount {
  font-size: 26px;
  font-weight: 800;
  color: #1e1e1e;
  margin: 8px 0 16px;
}

.best-day-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.best-day-grid div {
  background: #f8f8f8;
  border-radius: 14px;
  padding: 12px;
}

.best-day-grid span {
  display: block;
  font-size: 12px;
  color: #6b7280;
}

.best-day-grid strong {
  font-size: 14px;
}

.growth-box {
  border-radius: 14px;
  padding: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.growth-up {
  background: rgba(22, 163, 74, 0.1);
  color: #15803d;
}

.growth-down {
  background: rgba(235, 61, 96, 0.1);
  color: #eb3d60;
}

.highlight-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 16px;
  padding: 16px;
  min-height: 92px;
  box-shadow: 0 6px 18px rgba(30, 30, 30, 0.05);
}

.highlight-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.highlight-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
}

.highlight-value {
  display: block;
  font-size: 16px;
  color: #1e1e1e;
}
</style>
